import * as THREE from 'three';
import {AbstractPointSnapStrategy} from './AbstractPointSnapStrategy';
import type {PointSnapRequest, PointSnapResult} from './PointSnapTypes';
import {
    LineMaterialFactory,
    syncScreenSpaceDashedLineGeometry,
} from '../../material';
import type {ScreenSpaceDashedLineNodeMaterial} from '../../material';
import type {AbstractSketchEdgeObject} from '../../mesh/sketch/AbstractSketchEdgeObject';
import {scheduleDisposableResourceDisposal} from '../../utils';

/**
 * 方向引导线信息。
 */
interface DirectionalGuideLineInfo {
    /** 引导线渲染对象。 */
    line: THREE.Line;
    /** 引导线归一化方向。 */
    direction: THREE.Vector3;
    /** 普通状态屏幕空间虚线材质。 */
    normalMaterial: ScreenSpaceDashedLineNodeMaterial;
    /** 高亮状态屏幕空间虚线材质。 */
    highlightMaterial: ScreenSpaceDashedLineNodeMaterial;
}

/**
 * 方向引导捕捉公共父类。
 *
 * 设计说明：
 * 1. 平行、垂直捕捉都遵循“真实悬停源几何 -> 生成方向引导线 -> 鼠标贴近引导线后吸附”的流程；
 * 2. 公共父类只管理引导线生命周期、材质创建、吸附距离判定和资源释放；
 * 3. 子类只负责决定哪些 hoveredObject 可以作为源几何，以及如何从源几何计算一条或多条引导方向。
 */
export abstract class AbstractDirectionalGuidePointSnapStrategy<
    TTarget extends AbstractSketchEdgeObject = AbstractSketchEdgeObject,
> extends AbstractPointSnapStrategy {
    /** 引导线信息列表。 */
    private guideLineInfos: DirectionalGuideLineInfo[] = [];

    /** 上一次生成引导线的源对象。 */
    private lastTargetObject: THREE.Object3D | null = null;

    /** 上一次生成引导线时使用的绘制起点。 */
    private lastGuideFirstPoint: THREE.Vector3 | null = null;

    /** 引导线吸附屏幕像素阈值。 */
    protected abstract readonly guideScreenDistanceThresholdPx: number;

    /** 捕捉结果权重。 */
    protected abstract readonly guideSnapWeight: number;

    /** 引导线普通状态颜色。 */
    protected abstract readonly guidePreviewLineColor: THREE.ColorRepresentation;

    /** 引导线高亮状态颜色。 */
    protected abstract readonly guideHighlightLineColor: THREE.ColorRepresentation;

    /** 引导线屏幕空间虚线段长度。 */
    protected abstract readonly guideScreenSpaceDashSizePx: number;

    /** 引导线屏幕空间虚线间隔长度。 */
    protected abstract readonly guideScreenSpaceGapSizePx: number;

    /** 普通状态材质名称。 */
    protected abstract readonly guidePreviewMaterialName: string;

    /** 高亮状态材质名称。 */
    protected abstract readonly guideHighlightMaterialName: string;

    /**
     * 执行方向引导捕捉。
     *
     * @param request 点捕捉请求参数。
     * @returns 捕捉结果；未命中时返回 null。
     */
    public resolve(request: PointSnapRequest): PointSnapResult | null {
        const firstPoint = request.drawingFirstPoint ?? null;
        // 方向型引导线必须依赖绘制起点，没有起点时不能保留旧引导线。
        if (!firstPoint) {
            this.clearPreview();
            return null;
        }

        const {screenPosition: screenPos, worldPosition: rawPoint} = request;
        // 绘制起点变化时，既有引导线的屏幕表现和实际吸附线都会失效，必须先清理后再按当前悬停目标重建。
        if (this.lastGuideFirstPoint && !this.lastGuideFirstPoint.equals(firstPoint)) {
            this.clearPreview();
        }

        const hoveredTarget = this.resolveGuideTarget(request);

        // 只有真实悬停目标变化时才重建引导线，鼠标移出空白区域时继续复用已有引导线。
        if (hoveredTarget && hoveredTarget !== this.lastTargetObject) {
            this.clearPreview();
            const hasGuideLine = this.buildGuideLines(firstPoint, hoveredTarget);
            if (hasGuideLine) {
                this.lastTargetObject = hoveredTarget;
                this.lastGuideFirstPoint = firstPoint.clone();
            }
        }

        // 每帧只根据当前鼠标到引导线的屏幕距离切换材质，不重建几何。
        this.updateGuideHighlightState(firstPoint, rawPoint, screenPos);

        const snapPoint = this.getSnapPreviewPoint(firstPoint, rawPoint, screenPos);
        if (!snapPoint) {
            return null;
        }

        return {
            strategyType: this.strategyType,
            position: snapPoint,
            indicatorPosition: snapPoint,
            targetObject: null,
            weight: this.guideSnapWeight,
        };
    }

    /**
     * 清除方向引导线并释放资源。
     */
    public override clearPreview(): void {
        if (this.guideLineInfos.length === 0) {
            this.lastTargetObject = null;
            this.lastGuideFirstPoint = null;
            return;
        }

        for (const info of this.guideLineInfos) {
            this.scene?.remove(info.line);
            scheduleDisposableResourceDisposal(info.line.geometry);
            scheduleDisposableResourceDisposal(info.normalMaterial);
            scheduleDisposableResourceDisposal(info.highlightMaterial);
        }

        this.guideLineInfos = [];
        this.lastTargetObject = null;
        this.lastGuideFirstPoint = null;
    }

    /**
     * 从请求中解析当前可作为引导线来源的悬停对象。
     *
     * @param request 点捕捉请求。
     * @returns 可作为源几何的对象；无合法源对象时返回 null。
     */
    protected abstract resolveGuideTarget(request: PointSnapRequest): TTarget | null;

    /**
     * 根据源几何计算引导线方向。
     *
     * @param firstPoint 绘制起点。
     * @param target 源几何对象。
     * @returns 一条或多条未归一化引导线方向。
     */
    protected abstract createGuideDirections(firstPoint: THREE.Vector3, target: TTarget): THREE.Vector3[];

    /**
     * 根据源对象重建所有引导线。
     *
     * @param firstPoint 绘制起点。
     * @param target 源几何对象。
     * @returns 至少成功创建一条引导线时返回 true。
     */
    private buildGuideLines(firstPoint: THREE.Vector3, target: TTarget): boolean {
        const directions = this.createGuideDirections(firstPoint, target);
        let createdCount = 0;
        for (const direction of directions) {
            if (this.buildOneGuideLine(firstPoint, direction)) {
                createdCount += 1;
            }
        }
        return createdCount > 0;
    }

    /**
     * 创建单条方向引导线。
     *
     * @param firstPoint 绘制起点。
     * @param rawDirection 未归一化方向。
     * @returns 成功创建引导线时返回 true。
     */
    private buildOneGuideLine(firstPoint: THREE.Vector3, rawDirection: THREE.Vector3): boolean {
        const direction = rawDirection.clone();
        if (direction.lengthSq() < 1e-18) {
            return false;
        }
        direction.normalize();

        const ptA = firstPoint.clone().addScaledVector(direction, -this.pointSnapGuideLineLength);
        const ptB = firstPoint.clone().addScaledVector(direction, this.pointSnapGuideLineLength);
        const points = [ptA, ptB];
        const geometry = new THREE.BufferGeometry().setFromPoints(points);
        const normalMaterial = this.createGuideMaterial(points, this.guidePreviewLineColor, this.guidePreviewMaterialName);
        const highlightMaterial = this.createGuideMaterial(points, this.guideHighlightLineColor, this.guideHighlightMaterialName);
        const line = new THREE.Line(geometry, normalMaterial);

        // 屏幕空间虚线材质依赖几何距离百分比，创建后必须立即同步。
        syncScreenSpaceDashedLineGeometry(line);
        this.addPreviewObject(line);

        this.guideLineInfos.push({
            line,
            direction,
            normalMaterial,
            highlightMaterial,
        });
        return true;
    }

    /**
     * 创建方向引导线材质。
     *
     * @param points 引导线点列。
     * @param color 材质颜色。
     * @param materialName 材质名称。
     * @returns 屏幕空间虚线材质。
     */
    private createGuideMaterial(
        points: THREE.Vector3[],
        color: THREE.ColorRepresentation,
        materialName: string,
    ): ScreenSpaceDashedLineNodeMaterial {
        return LineMaterialFactory.createScreenSpaceDashedLineMaterial({
            points,
            color,
            linewidth: 2,
            dashSize: this.guideScreenSpaceDashSizePx,
            gapSize: this.guideScreenSpaceGapSizePx,
            opacity: 0.85,
            transparent: true,
            name: materialName,
        });
    }

    /**
     * 根据鼠标到引导线的屏幕距离更新高亮状态。
     *
     * @param firstPoint 绘制起点。
     * @param rawPoint 当前鼠标世界坐标。
     * @param screenPos 当前鼠标屏幕坐标。
     */
    private updateGuideHighlightState(firstPoint: THREE.Vector3, rawPoint: THREE.Vector3, screenPos: THREE.Vector2): void {
        if (this.guideLineInfos.length === 0) {
            return;
        }

        for (const info of this.guideLineInfos) {
            const projected = this.resolveProjectedLineScreenDistance(firstPoint, rawPoint, info.direction, screenPos);
            info.line.material = projected !== null && projected.screenDistance <= this.guideScreenDistanceThresholdPx
                ? info.highlightMaterial
                : info.normalMaterial;
        }
    }

    /**
     * 获取当前吸附点。
     *
     * @param firstPoint 绘制起点。
     * @param rawPoint 当前鼠标世界坐标。
     * @param screenPos 当前鼠标屏幕坐标。
     * @returns 投影吸附点；无命中时返回 null。
     */
    private getSnapPreviewPoint(firstPoint: THREE.Vector3, rawPoint: THREE.Vector3, screenPos: THREE.Vector2): THREE.Vector3 | null {
        if (this.guideLineInfos.length === 0) {
            return null;
        }

        let bestPoint: THREE.Vector3 | null = null;
        let bestDistance = Infinity;

        for (const info of this.guideLineInfos) {
            const projected = this.resolveProjectedLineScreenDistance(firstPoint, rawPoint, info.direction, screenPos);
            if (!projected) {
                continue;
            }
            if (projected.screenDistance <= this.guideScreenDistanceThresholdPx && projected.screenDistance < bestDistance) {
                bestDistance = projected.screenDistance;
                bestPoint = projected.projectedWorld;
            }
        }

        return bestPoint;
    }
}


